import React from 'react';
import { Col, FormGroup, Label } from 'reactstrap';
import { SelectApplicantsAddresses } from './SelectApplicantsAddresses';
import { Select, StyledColBold } from 'components/Common';
import { DELIVERY_INDIVIDUAL_METHODS } from './ApplicantMainInfo';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { ApplicantDeliveryForm } from './ApplicantDeliveryForm';
import { DangerHelpText } from 'components/HelpText';

export const ApplicantAddressForm = ({
  currentApplicant,
  index,
  isApplicantInWork,
}) => {
  const formik = useFormikContext();
  const { t } = useTranslation();

  const applicantAddresses = formik.values.applicants.filter(
    applicant =>
      applicant?.applicantDetail?.id !== currentApplicant?.applicantDetail.id &&
      applicant?.address &&
      applicant?.deliveryMethod === DELIVERY_INDIVIDUAL_METHODS[2].value,
  );

  const isRelatesApplicants = formik.values.applicants.some(
    applicant =>
      applicant.addressAnotherApplicant ===
      currentApplicant?.applicantDetail.id,
  );

  const isDisabledFields = !formik.values.orderDetail?.isEditable;

  // Get error state - show error if field has error AND (touched OR form was submitted)
  const fieldError = formik.errors.applicants?.[index]?.deliveryMethod;
  const isFieldTouched = formik.touched.applicants?.[index]?.deliveryMethod;
  const hasError = !!fieldError && (isFieldTouched || formik.submitCount > 0);

  return (
    <>
      <StyledColBold className="display-4">
        {t('delivery.method')}
      </StyledColBold>

      <FormGroup row className="align-items-center mt-4">
        <Col xs={12} xl={2} className="d-flex align-items-center">
          <Label>{t('delivery.deliveryMethodTxt')}</Label>
        </Col>

        <Col xs={12} xl={10}>
          <Select
            isClearable
            className="w-50"
            menuPosition="fixed"
            styles={{
              menuPortal: base => ({
                ...base,
                zIndex: 99999,
              }),
              menu: base => ({
                ...base,
                zIndex: 99999,
                maxHeight: '200px',
                overflowY: 'auto',
                borderRadius: '8px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                padding: '4px 0',
              }),
              option: (base, state) => ({
                ...base,
                padding: '8px 16px',
                cursor: 'pointer',
                backgroundColor: state.isFocused ? '#f0f4ff' : 'white',
                color: state.isFocused ? '#0d6efd' : '#2d3748',
                '&:active': {
                  backgroundColor: '#e0e8ff',
                },
              }),
              control: (base, state) => ({
                ...base,
                borderRadius: '6px',
                borderColor: hasError
                  ? '#dc3545'
                  : state.isFocused
                  ? '#0d6efd'
                  : '#e2e8f0',
                borderWidth: hasError ? '2px' : '1px',
                boxShadow: hasError
                  ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                  : state.isFocused
                  ? '0 0 0 0.25rem rgba(13, 110, 253, 0.15)'
                  : 'none',
                '&:hover': {
                  borderColor: hasError ? '#dc3545' : '#a0aec0',
                },
              }),
              placeholder: base => ({
                ...base,
                color: '#a0aec0',
              }),
            }}
            options={
              applicantAddresses.length > 0
                ? DELIVERY_INDIVIDUAL_METHODS
                : DELIVERY_INDIVIDUAL_METHODS.filter(
                    method =>
                      method.value !== DELIVERY_INDIVIDUAL_METHODS[1].value,
                  )
            }
            placeholder={t('order.methodPlaceholder')}
            isDisabled={
              isApplicantInWork || isRelatesApplicants || isDisabledFields
            }
            invalid={hasError}
            value={DELIVERY_INDIVIDUAL_METHODS.find(
              item => item.value === currentApplicant?.deliveryMethod,
            )}
            onChange={event => {
              formik.setFieldValue(
                `applicants[${index}].deliveryMethod`,
                event ? event.value : null,
              );
              formik.setFieldValue(`applicants[${index}].address`, null);
              formik.setFieldValue(
                `applicants[${index}].addressAnotherApplicant`,
                null,
              );

              if (event?.value === 'delivery_method_to_address') {
                formik.setFieldValue(
                  `applicants[${index}].address.mailBox`,
                  'yes',
                );
                formik.setFieldValue(
                  `applicants[${index}].address.deliveryMethod`,
                  event.value,
                );
              }

              // clean delivery services if delivery method won't be to address
              if (event?.value !== 'delivery_method_to_address') {
                const formikServices = formik.values.applicants[
                  index
                ].services.filter(
                  item => item.product_type.type !== 'delivery',
                );

                formik.setFieldValue(
                  `applicants[${index}].services`,
                  formikServices,
                );
              }
            }}
            classNamePrefix="react-select"
          />

          {/* Error message display */}
          {hasError && (
            <DangerHelpText noBackground>{fieldError}</DangerHelpText>
          )}
        </Col>
      </FormGroup>

      {currentApplicant?.deliveryMethod ===
        DELIVERY_INDIVIDUAL_METHODS[1].value && (
        <FormGroup row className="align-items-center mt-4">
          <Col lg={4} xxl={2} className="d-flex align-items-center">
            <Label className="display-5 m-0">
              {t('order.applicantsAddresses')}
            </Label>
          </Col>

          <Col lg={8} xxl={10}>
            <SelectApplicantsAddresses
              invalid={
                !!formik.errors.applicants?.[index]?.addressAnotherApplicant &&
                (formik.touched.applicants?.[index]?.addressAnotherApplicant ||
                  formik.submitCount > 0)
              }
              isDisabled={isApplicantInWork || isDisabledFields}
              onChange={option =>
                formik.setFieldValue(
                  `applicants[${index}].addressAnotherApplicant`,
                  option ? option.value : null,
                )
              }
              value={currentApplicant?.addressAnotherApplicant}
              applicantsWithAddresses={applicantAddresses}
            />
            {/* Error message for addressAnotherApplicant */}
            {!!formik.errors.applicants?.[index]?.addressAnotherApplicant &&
              (formik.touched.applicants?.[index]?.addressAnotherApplicant ||
                formik.submitCount > 0) && (
                <DangerHelpText noBackground>
                  {formik.errors.applicants?.[index]?.addressAnotherApplicant}
                </DangerHelpText>
              )}
          </Col>
        </FormGroup>
      )}

      {currentApplicant?.deliveryMethod ===
        DELIVERY_INDIVIDUAL_METHODS[2].value && (
        <ApplicantDeliveryForm
          disabled={isApplicantInWork || isDisabledFields}
          currentApplicant={currentApplicant}
          indexApplicant={index}
        />
      )}
    </>
  );
};
